class PasQuickViewProduct extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';
    try { this.variants = JSON.parse(this.querySelector('[data-quick-view-variants]')?.textContent || '[]'); }
    catch { this.variants = []; }
    this.input = this.querySelector('[data-quick-view-variant-id]');
    this.active = window.PasVariantState?.byId(this.variants, this.input?.value) || this.variants[0] || null;
    this.addEventListener('click', (event) => this.handleClick(event));
    this.querySelector('[data-quantity-input]')?.addEventListener('change', (event) => this.normalizeQuantity(event.currentTarget));
    this.refreshOptions();
  }

  handleClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const option = target.closest('[data-option-control]');
    if (option && !option.disabled) {
      const variant = window.PasVariantState?.resolve(this.variants, this.active, Number(option.dataset.optionIndex), option.dataset.optionValue);
      if (variant) this.selectVariant(variant);
      return;
    }
    const input = this.querySelector('[data-quantity-input]');
    if (target.closest('[data-quantity-minus]') && input) {
      input.value = String(Math.max(1, Number.parseInt(input.value, 10) - 1 || 1));
      return;
    }
    if (target.closest('[data-quantity-plus]') && input) {
      input.value = String(Math.max(1, Number.parseInt(input.value, 10) + 1 || 1));
    }
  }

  normalizeQuantity(input) {
    const value = Number.parseInt(input.value, 10);
    input.value = String(Number.isFinite(value) ? Math.max(1, value) : 1);
  }

  selectVariant(variant) {
    this.active = variant;
    if (this.input) this.input.value = variant.id;
    const price = this.querySelector('[data-quick-view-price]');
    const sku = this.querySelector('[data-quick-view-sku]');
    const availability = this.querySelector('[data-quick-view-availability]');
    const add = this.querySelector('[data-quick-view-add]');
    const image = this.querySelector('.pas-quick-view-product__image');
    const details = this.querySelector('[data-quick-view-details]');
    if (price) price.textContent = variant.price;
    if (sku) sku.textContent = variant.sku || '—';
    if (availability) availability.textContent = variant.available ? 'In stock' : 'Sold out';
    if (add) { add.disabled = !variant.available; add.textContent = variant.available ? 'Add to cart' : 'Sold out'; }
    if (details) details.href = variant.url;
    if (image && variant.image) { image.removeAttribute('srcset'); image.src = variant.image; image.alt = variant.imageAlt || ''; }
    this.refreshOptions();
  }

  refreshOptions() {
    this.querySelectorAll('[data-option-control]').forEach((control) => {
      const index = Number(control.dataset.optionIndex);
      const value = control.dataset.optionValue;
      const candidate = window.PasVariantState?.resolve(this.variants, this.active, index, value);
      const active = this.active?.options[index] === value;
      const unavailable = !candidate || !candidate.available;
      control.disabled = unavailable && !active;
      control.classList.toggle('is-active', active);
      control.classList.toggle('is-unavailable', unavailable);
      control.setAttribute('aria-checked', String(active));
      control.setAttribute('aria-disabled', String(unavailable && !active));
      const name = control.closest('[role="radiogroup"]')?.getAttribute('aria-label') || 'Option';
      control.setAttribute('aria-label', `${name}: ${value}${unavailable ? candidate ? ' (sold out)' : ' (unavailable combination)' : ''}`);
      const optionPrice = control.querySelector('[data-option-price]');
      if (optionPrice) optionPrice.textContent = candidate?.price || '';
    });
    this.querySelectorAll('[data-option-selected]').forEach((label) => {
      label.textContent = this.active?.options[Number(label.dataset.optionSelected)] || '';
    });
  }
}

if (!customElements.get('pas-quick-view-product')) customElements.define('pas-quick-view-product', PasQuickViewProduct);

class PasQuickViewController {
  constructor(root) {
    this.root = root;
    this.dialog = root.querySelector('[role="dialog"]');
    this.content = root.querySelector('[data-quick-view-content]');
    this.status = root.querySelector('[data-quick-view-status]');
    this.lastFocus = null;
    document.addEventListener('click', (event) => this.handleClick(event));
    document.addEventListener('keydown', (event) => this.handleKeydown(event));
    document.addEventListener('pas:cart-added', () => this.close(false));
  }

  handleClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    const trigger = target?.closest('[data-quick-view-trigger]');
    if (trigger) { event.preventDefault(); void this.open(trigger); return; }
    if (target?.closest('[data-quick-view-close]')) this.close();
  }

  async open(trigger) {
    this.lastFocus = trigger;
    this.root.hidden = false;
    this.root.setAttribute('aria-hidden', 'false');
    this.root.classList.add('is-loading');
    this.content.replaceChildren();
    this.status.textContent = 'Loading product…';
    document.documentElement.classList.add('pas-lock-scroll');
    requestAnimationFrame(() => { this.root.classList.add('is-open'); this.dialog.focus(); });

    try {
      const url = new URL(trigger.dataset.productUrl, window.location.origin);
      url.searchParams.set('section_id', 'pas-quick-view');
      if (trigger.dataset.initialVariant) url.searchParams.set('variant', trigger.dataset.initialVariant);
      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Product could not be loaded.');
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const product = page.querySelector('pas-quick-view-product');
      if (!product) throw new Error('Product could not be loaded.');
      this.content.replaceChildren(product);
      this.status.textContent = '';
      this.root.classList.remove('is-loading');
      product.querySelector('[data-option-control], [data-quick-view-add], a')?.focus();
    } catch (error) {
      this.root.classList.remove('is-loading');
      this.status.textContent = error instanceof Error ? error.message : 'Product could not be loaded.';
    }
  }

  close(restore = true) {
    if (this.root.hidden) return;
    this.root.classList.remove('is-open');
    this.root.setAttribute('aria-hidden', 'true');
    window.setTimeout(() => {
      if (this.root.classList.contains('is-open')) return;
      this.root.hidden = true;
      this.content.replaceChildren();
    }, 400);
    if (!document.querySelector('[data-cart-drawer].is-open')) document.documentElement.classList.remove('pas-lock-scroll');
    if (restore) this.lastFocus?.focus?.();
  }

  handleKeydown(event) {
    if (!this.root.classList.contains('is-open')) return;
    if (event.key === 'Escape') { event.preventDefault(); this.close(); return; }
    if (event.key !== 'Tab') return;
    const focusable = [...this.dialog.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])')];
    if (!focusable.length) { event.preventDefault(); this.dialog.focus(); return; }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }
}

const pasQuickViewRoot = document.querySelector('[data-quick-view]');
if (pasQuickViewRoot) window.PasQuickView = new PasQuickViewController(pasQuickViewRoot);
